import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '@/api/notifications';
import { NotificationPreferences, NotificationPreferencesUpdate } from '@/types/api';
import { toast } from 'sonner';

// Query keys
export const notificationKeys = {
  all: ['notifications'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
};

// Hook to get current user's notification preferences
export const useNotificationPreferences = () => {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: notificationsApi.getPreferences,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401 (unauthorized) or 404 (not found)
      if ((error as { response?: { status?: number } })?.response?.status === 401 || 
          (error as { response?: { status?: number } })?.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Hook to update notification preferences
export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: NotificationPreferencesUpdate) => notificationsApi.updatePreferences(data),
    onSuccess: (data) => {
      // Update the cached preferences data
      queryClient.setQueryData(notificationKeys.preferences(), data);
      toast.success('Notification preferences updated successfully');
    },
    onError: (error) => {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to update notification preferences';
      toast.error(errorMessage);
    },
  });
};

